'use client';

import * as React from 'react';

import { DocumentPreviewToolbarActions } from './document-preview-toolbar-actions';
import { PDFViewer } from './extend-ui/ui/pdf-viewer';
import { PreviewError, PreviewStatus } from './preview-state';

export function PdfArtifactPreview({
  fileUrl,
  fitToPaneRequestId,
  onClose,
  onUserZoomChange,
  sourceUrl,
  title,
}: {
  fileUrl: string;
  fitToPaneRequestId?: number;
  onClose: () => void;
  onUserZoomChange?: () => void;
  sourceUrl: string;
  title: string;
}) {
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const abortController = new AbortController();
    let currentObjectUrl: string | null = null;

    setObjectUrl(null);
    setLoadError(null);

    fetch(sourceUrl, { signal: abortController.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const blob = new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
        currentObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(currentObjectUrl);
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        setLoadError(error instanceof Error ? error.message : 'Unable to fetch PDF.');
      });

    return () => {
      abortController.abort();

      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [sourceUrl]);

  if (loadError) {
    return <PreviewError message={loadError} />;
  }

  if (!objectUrl) {
    return <PreviewStatus>Loading PDF preview...</PreviewStatus>;
  }

  return (
    <PDFViewer
      className="h-full min-h-[480px]"
      fitToPaneRequestId={fitToPaneRequestId}
      fileName={title}
      forceInlineThumbnailSidebar
      onUserZoomChange={onUserZoomChange}
      showDownload={false}
      showRotateControls={false}
      showToolbar
      showUpload={false}
      src={objectUrl}
      toolbarActions={
        <DocumentPreviewToolbarActions filename={title} fileUrl={fileUrl} onClose={onClose} showOpenInNewTab />
      }
      toolbarMode="minimal"
    />
  );
}
